/**
 * Hook for managing map theming
 *
 * Provides CSS variables and tile URLs based on MapTheme configuration.
 * Used by MapSettingsModal and MapView components.
 */

import { useMemo } from 'react';
import type { MapTheme, TileStyle, MarkerStyle } from '@love-map/shared';
import { DEFAULT_MAP_THEME } from '@love-map/shared';

/**
 * Tile URL configurations for different map styles
 */
export const TILE_URLS: Record<TileStyle, string> = {
  dark: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  light: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
  satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  terrain: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
};

/**
 * Preset custom tile providers for easy selection
 */
export interface TilePreset {
  name: string;
  url: string;
  attribution: string;
  requiresApiKey?: boolean;
}

export const TILE_PRESETS: TilePreset[] = [
  {
    name: 'OpenStreetMap',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  {
    name: 'Stamen Watercolor',
    url: 'https://tiles.stadiamaps.com/tiles/stamen_watercolor/{z}/{x}/{y}.jpg',
    attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://stamen.com/">Stamen Design</a>',
  },
  {
    name: 'CartoDB Voyager',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  {
    name: 'CartoDB Positron',
    url: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  {
    name: 'Stamen Toner',
    url: 'https://tiles.stadiamaps.com/tiles/stamen_toner/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://stamen.com/">Stamen Design</a>',
  },
];

/**
 * Validates a tile URL format
 * Must contain {z}, {x}, {y} placeholders
 */
export function isValidTileUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  // Check for required placeholders
  const hasZ = url.includes('{z}');
  const hasX = url.includes('{x}');
  const hasY = url.includes('{y}');
  // Basic URL format check
  const isUrl = url.startsWith('https://') || url.startsWith('http://');
  return isUrl && hasZ && hasX && hasY;
}

/**
 * Tile attribution for different map styles
 */
export const TILE_ATTRIBUTIONS: Record<TileStyle, string> = {
  dark: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  light: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  satellite: '&copy; <a href="https://www.esri.com/">Esri</a>',
  terrain: '&copy; <a href="https://opentopomap.org/">OpenTopoMap</a> contributors',
};

/**
 * Display labels for tile styles
 */
export const TILE_STYLE_LABELS: Record<TileStyle, string> = {
  dark: 'Dark',
  light: 'Light',
  satellite: 'Satellite',
  terrain: 'Terrain',
};

/**
 * Display labels for marker styles
 */
export const MARKER_STYLE_LABELS: Record<MarkerStyle, string> = {
  default: 'Default',
  minimal: 'Minimal',
  colorful: 'Colorful',
};

/**
 * Convert hex color to RGB string
 */
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '139, 92, 246'; // default purple
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
}

/**
 * Darken a hex color by a percentage
 */
function darkenColor(hex: string, percent: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;

  const r = Math.max(0, Math.floor(parseInt(result[1], 16) * (1 - percent / 100)));
  const g = Math.max(0, Math.floor(parseInt(result[2], 16) * (1 - percent / 100)));
  const b = Math.max(0, Math.floor(parseInt(result[3], 16) * (1 - percent / 100)));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export interface UseMapThemeResult {
  /** CSS custom properties to apply for theming */
  cssVariables: Record<string, string>;
  /** Tile URL for the current tile style */
  tileUrl: string;
  /** Tile attribution for the current tile style */
  tileAttribution: string;
  /** Current theme configuration */
  theme: MapTheme;
  /** Whether using a light-background tile style */
  isLightTheme: boolean;
}

/**
 * Hook for managing map theming
 *
 * @param theme - Optional theme configuration. Falls back to DEFAULT_MAP_THEME.
 * @returns Theme-related values and CSS variables
 *
 * @example
 * ```tsx
 * const { cssVariables, tileUrl, theme } = useMapTheme(map?.settings?.theme);
 *
 * // Apply CSS variables to container
 * <div style={cssVariables}>
 *   <MapContainer>
 *     <TileLayer url={tileUrl} />
 *   </MapContainer>
 * </div>
 * ```
 */
export function useMapTheme(theme?: Partial<MapTheme>): UseMapThemeResult {
  const mergedTheme: MapTheme = useMemo(() => ({
    primaryColor: theme?.primaryColor ?? DEFAULT_MAP_THEME.primaryColor,
    tileStyle: theme?.tileStyle ?? DEFAULT_MAP_THEME.tileStyle,
    markerStyle: theme?.markerStyle ?? DEFAULT_MAP_THEME.markerStyle,
    customTileUrl: theme?.customTileUrl,
  }), [theme?.primaryColor, theme?.tileStyle, theme?.markerStyle, theme?.customTileUrl]);

  const cssVariables = useMemo(() => {
    const primary = mergedTheme.primaryColor;
    const primaryRgb = hexToRgb(primary);
    const highlighted = darkenColor(primary, 20);

    return {
      '--theme-primary': primary,
      '--theme-primary-rgb': primaryRgb,
      '--theme-highlighted': highlighted,
      // Override layer colors with theme primary for unified look
      '--layer-travels-primary': primary,
      '--layer-travels-primary-rgb': primaryRgb,
      '--layer-travels-highlighted': highlighted,
    };
  }, [mergedTheme.primaryColor]);

  const tileUrl = useMemo(() => {
    // Use custom tile URL if set and valid
    if (mergedTheme.customTileUrl && isValidTileUrl(mergedTheme.customTileUrl)) {
      return mergedTheme.customTileUrl;
    }
    return TILE_URLS[mergedTheme.tileStyle] || TILE_URLS.dark;
  }, [mergedTheme.tileStyle, mergedTheme.customTileUrl]);

  const tileAttribution = useMemo(() => {
    // If using custom tile URL, check if it matches a preset for attribution
    if (mergedTheme.customTileUrl && isValidTileUrl(mergedTheme.customTileUrl)) {
      const preset = TILE_PRESETS.find(p => p.url === mergedTheme.customTileUrl);
      if (preset) return preset.attribution;
      // Default attribution for unknown custom URLs
      return '&copy; Map data contributors';
    }
    return TILE_ATTRIBUTIONS[mergedTheme.tileStyle] || TILE_ATTRIBUTIONS.dark;
  }, [mergedTheme.tileStyle, mergedTheme.customTileUrl]);

  const isLightTheme = mergedTheme.tileStyle === 'light' || mergedTheme.tileStyle === 'satellite';

  return {
    cssVariables,
    tileUrl,
    tileAttribution,
    theme: mergedTheme,
    isLightTheme,
  };
}
